import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Safely sanitizes HTML content to prevent XSS attacks
 * @param {string} html - The HTML content to sanitize
 * @returns {string} The sanitized HTML content
 */
export const sanitizeHtml = (html) => {
    if (!html || typeof html !== 'string') {
        return '';
    }
    
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
            'b', 'i', 'em', 'strong', 'u', 'br', 'p', 'span', 'div',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li',
            'a', 'blockquote', 'cite'
        ],
        ALLOWED_ATTR: ['class', 'style', 'href', 'target'],
        KEEP_CONTENT: true,
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'iframe'],
        FORBID_ATTR: [
            'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout',
            'onmousedown', 'onmouseup', 'onkeydown', 'onkeyup', 'onkeypress',
            'onfocus', 'onblur', 'onchange', 'onsubmit', 'onreset'
        ]
    });
};

/**
 * Escapes HTML content by stripping all tags and converting entities to plain text
 * @param {string} html - The HTML content to escape
 * @returns {string} Plain text without HTML tags
 */
export const escapeHtml = (html) => {
    if (!html || typeof html !== 'string') {
        return '';
    }
    
    // First sanitize to remove dangerous content, then strip all tags
    const sanitized = DOMPurify.sanitize(html, { 
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true 
    });
    
    // Create a temporary element to decode HTML entities safely
    const tempElement = document.createElement('div');
    // Use textContent instead of innerHTML to avoid XSS vulnerabilities
    tempElement.textContent = sanitized;
    
    // Create another element to properly decode HTML entities
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitized, 'text/html');
    return doc.body.textContent || doc.body.innerText || '';
};

/**
 * Sanitizes and validates URLs to prevent XSS attacks through href attributes
 * @param {string} url - The URL to sanitize
 * @returns {string|null} Safe URL or null if invalid
 */
export const sanitizeUrl = (url) => {
    if (!url || typeof url !== 'string') {
        return null;
    }

    // Remove any whitespace
    const trimmedUrl = url.trim();
    
    if (!trimmedUrl) {
        return null;
    }
    
    // Check for obviously malicious patterns
    const dangerousPatterns = [
        /javascript:/i,
        /data:/i,
        /vbscript:/i,
        /file:/i,
        /about:/i,
        /chrome:/i,
        /moz-extension:/i,
        /chrome-extension:/i,
        /ms-appx:/i
    ];

    for (const pattern of dangerousPatterns) {
        if (pattern.test(trimmedUrl)) {
            return null;
        }
    }

    // Allow only http, https, mailto, tel, ftp protocols and relative URLs
    const allowedProtocolPattern = /^(https?:\/\/|mailto:|tel:|ftp:\/\/|\/|\.\/|#)/i;
    
    if (!allowedProtocolPattern.test(trimmedUrl)) {
        return null;
    }

    try {
        // For absolute URLs, validate with URL constructor
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
            // Validate URL format - will throw if invalid
            const validatedUrl = new URL(trimmedUrl);
            // We only need validation, but ESLint requires we use the result
            if (!validatedUrl) {
                return null;
            }
        }
        
        // Return the original URL without HTML encoding for href usage
        // The browser will handle proper encoding in the href context
        return trimmedUrl;
    } catch (error) {
        // Invalid URL
        return null;
    }
};

/**
 * Renders a safe link component that validates URLs and gracefully degrades
 * @param {string} href - The URL to link to
 * @param {React.ReactNode} children - The content of the link
 * @param {string} className - CSS classes to apply
 * @param {object} additionalProps - Additional props to pass to the element
 * @param {boolean} openInNewTab - Whether to open link in new tab (default: true)
 * @returns {React.ReactElement} Safe link or span element
 */
export const renderSafeLink = (href, children, className = "", additionalProps = {}, openInNewTab = true) => {
    const safeUrl = sanitizeUrl(href);
    
    if (!safeUrl) {
        // If URL is invalid, render as span instead of link to prevent broken UX
        // Filter out link-specific attributes that don't make sense for span
        const { target, rel, href, ...spanProps } = additionalProps;
        return React.createElement('span', {
            className,
            ...spanProps
        }, children);
    }
    
    const linkProps = {
        href: safeUrl,
        className,
        ...additionalProps
    };
    
    // Add security attributes for external links
    if (openInNewTab) {
        linkProps.target = '_blank';
        linkProps.rel = 'noopener noreferrer';
    }
    
    return React.createElement('a', linkProps, children);
};

/**
 * Safely validates and sanitizes numeric values from potentially untrusted sources
 * @param {any} value - The value to validate as a number
 * @param {number} defaultValue - Default value if validation fails (default: 0)
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {number} Safe numeric value
 */
export const sanitizeNumeric = (value, defaultValue = 0, min = null, max = null) => {
    const parsed = parseInt(value, 10);
    
    if (isNaN(parsed)) {
        return defaultValue;
    }
    
    let result = parsed;
    
    if (min !== null && result < min) {
        result = min;
    }
    
    if (max !== null && result > max) {
        result = max;
    }
    
    return result;
};

/**
 * Creates a sanitized dangerouslySetInnerHTML object with enhanced security
 * @param {string} html - The HTML content to sanitize
 * @returns {object} Object with __html property containing sanitized content
 */
export const createSanitizedInnerHTML = (html) => {
    if (!html || typeof html !== 'string') {
        return { __html: '' };
    }
    
    return {
        __html: DOMPurify.sanitize(html, {
            ALLOWED_TAGS: [
                'b', 'i', 'em', 'strong', 'u', 'br', 'p', 'span', 'div',
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'ul', 'ol', 'li', 'a', 'blockquote', 'cite'
            ],
            ALLOWED_ATTR: ['class', 'style', 'href', 'target'],
            KEEP_CONTENT: true,
            ALLOW_DATA_ATTR: false,
            FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'iframe'],
            FORBID_ATTR: [
                'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout',
                'onmousedown', 'onmouseup', 'onkeydown', 'onkeyup', 'onkeypress',
                'onfocus', 'onblur', 'onchange', 'onsubmit', 'onreset'
            ],
            AFTER_SANITIZE_ATTRIBUTES: (node) => {
                // Sanitize href attributes
                if (node.hasAttribute && node.hasAttribute('href')) {
                    const href = node.getAttribute('href');
                    if (href) {
                        const sanitizedHref = sanitizeUrl(href);
                        if (sanitizedHref) {
                            node.setAttribute('href', sanitizedHref);
                        } else {
                            node.removeAttribute('href');
                        }
                    }
                }
            }
        })
    };
};
